import * as yup from 'yup';
import { cx } from '@emotion/css';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'next-i18next';
import { DSButton, DSCheckbox, DSHeading, DSText } from '@stihl-design-system/components';

import { LinkOut } from '@phoenix/core/frontend/shared/ui/link-out/link-out';
import { HookFormBaseForm } from '@phoenix/core/frontend/shared/ui/hook-form';
import { useUserLocale } from '@phoenix/core/frontend/shared/util/locales/use-user-locale';
import { getLocale } from '@phoenix/core/frontend/shared/util/locales/locales';
import { AnswerFormat, RaffleParticipationQuestion } from '@phoenix/team-m/my-tool-shed/frontend/domain/model/benefits.model';

import { RaffleQuestions } from './raffle-questions';
import { EnterRaffleVariables } from './use-raffle-participation';

interface RaffleFormProps {
  raffleId: string;
  className?: string;
  questions?: RaffleParticipationQuestion[];
  enterRaffle: ({ raffleId, newsletterConsent, answers }: EnterRaffleVariables) => void;
  isSubmitting?: boolean;
}

interface FormValues {
  marketingConsent: boolean;
}

export const RaffleForm: React.FC<RaffleFormProps> = ({ className, raffleId, enterRaffle, isSubmitting, questions }) => {
  const { t } = useTranslation();
  const locale = useUserLocale() || getLocale();

  const questionsSchema = questions?.reduce((acc, field) => {
    if (field.type === AnswerFormat.Boolean) {
      acc[`question_${field.id}`] = yup.boolean().required(t('shared:global_validationMessage_required'));
    }
    if (field.type === AnswerFormat.Text || field.type === AnswerFormat.SingleSelect) {
      acc[`question_${field.id}`] = yup.string().required(t('shared:global_validationMessage_required'));
    }

    return acc;
  }, {});

  const schema = yup.object().shape({
    ...questionsSchema,
    newsletterConsent: yup.boolean().optional(),
    termsConditions: yup.boolean().test({
      test: (value) => !!value,
      message: t('toolShed_myEquipment_raffle_checkboxErrorMessageStep2'),
    }),
  });

  const methods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
  });

  const onSubmit = (formValues: FormValues) => {
    const answers = Object.entries(formValues)
      .filter(([key]) => key.startsWith('question'))
      .map(([key, value]) => {
        const id = key.replace('question_', '');
        const type = questions?.find((item) => item?.id === id)?.type;
        return { id, type, value: String(value) };
      });

    const newsletterConsent = formValues['newsletterConsent'];
    enterRaffle({ raffleId, newsletterConsent, answers });
  };

  return (
    <HookFormBaseForm
      methods={methods}
      onSubmit={methods.handleSubmit(onSubmit)}
      className={cx('px-4 py-6 tablet:p-6 desktop:px-10 desktop:py-8 flex flex-col tablet:flex-row gap-6 tablet:gap-12', className)}
    >
      {!!questions?.length && <RaffleQuestions questions={questions} />}
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <DSHeading tag="h3" size="small" className="text-wrap">
            {t('toolShed_myEquipment_raffle_subheaderStep2')}
          </DSHeading>
          <Controller
            name="termsConditions"
            render={({ field, fieldState }) => (
              <DSCheckbox
                {...field}
                id={field.name}
                className="mt-5"
                invalid={fieldState.invalid}
                systemFeedback={fieldState.error?.message}
                data-testid="terms-conditions"
                label={
                  (
                    <Trans
                      i18nKey="toolShed_myEquipment_raffle_checkboxAcceptStep2"
                      components={{
                        termsLink: <LinkOut variant="highlight" href={`/${locale}/tool-shed/legal-conditions/raffle?id=${raffleId}`} />,
                      }}
                    />
                  ) as unknown as string
                }
              />
            )}
          />
          <Controller
            name="newsletterConsent"
            render={({ field }) => (
              <DSCheckbox
                {...field}
                className="mt-4"
                aria-details="newsletter-consent"
                label={t('toolShed_myEquipment_raffle_checkboxNewsStep2')}
              />
            )}
          />
          <DSText id="newsletter-consent" className="mt-2 text-secondary-dark" size="x-small">
            {t('toolShed_myEquipment_raffle_descriptionNewsStep2')}
          </DSText>
        </div>
        <DSButton className="mt-5 w-full tablet:w-fit" loading={isSubmitting}>
          {t('toolShed_myEquipment_raffle_buttonSubmit')}
        </DSButton>
      </div>
    </HookFormBaseForm>
  );
};
